<template>
  <div class="body-wrapper section content has-text-justified">
    <section>
      <h1 class="title is-2">Google Pay&#8482; Web Integration</h1>

      <p>
        By using Google Pay, you agree to all Google Pay terms and conditions.
        See the following links for more information:
      </p>
      <p>
        <a target="_blank" href="https://payments.developers.google.com/terms/aup">Acceptable Use Policy</a><br/>
        <a target="_blank" href="https://payments.developers.google.com/terms/sellertos">
          Google Pay API Terms of Service
        </a>
      </p>

      <p>
        The following card brands are supported with Google Pay:
      </p>
      <ul>
        <li>Amex</li>
        <li>MasterCard</li>
        <li>Discover</li>
        <li>JCB</li>
        <li>Visa</li>
      </ul>
    </section>

    <section>
      <h3>Integration steps:</h3>

      <ol class="bottom-margin">
        <li>
          <h4>Set up the merchant account</h4>
          <p>
            To use Google Pay, you must first register as a merchant with Google Pay.
            To setup a merchant account, visit the
            <a target="_blank" href="https://pay.google.com/business/console">Google Pay and Wallet Console</a>.
            <br/>
            Once you have set up your merchant account with Google Pay, you will need your merchant ID.
            To learn how to find your merchant ID, visit
            <a target="_blank" href="https://support.google.com/paymentscenter/answer/7163092?hl=en">this page</a>.
          </p>
        </li>
        <li>
          <h4>Adding Google Pay</h4>
          <p>
            Follow the
            <a target="_blank" href="https://developers.google.com/pay/api/web/overview">
              Google Pay Web documentation
            </a>
            to add a Google Pay button to your web page.
          </p>
          <p>
            The following authentication methods are supported for the <code>allowedAuthMethods</code> field in the
            <a target="_blank"
               href="https://developers.google.com/pay/api/web/reference/request-objects#CardParameters">
              Card Parameters
            </a> object:
          </p>
          <ul>
            <li>
              <code>"PAN_ONLY"</code>
              (3DS is not currently supported when using <code>"PAN_ONLY"</code>)
            </li>
            <li><code>"CRYPTOGRAM_3DS"</code></li>
          </ul>
          <p>
            You can require your customers to add billing info in the
            <a target="_blank"
               href="https://developers.google.com/pay/api/web/reference/request-objects#CardParameters">
              Card Parameters
            </a>
            object by setting the <code>billingAddressRequired</code> and <code>billingAddressParameters</code> fields.
          </p>
          <pre>
{
  "billingAddressRequired": true
  "billingAddressParameters": {
    "format": "FULL",
    "phoneNumberRequired": false
  }
}
            </pre>
          <p>
            When building the
            <a target="_blank" href="https://developers.google.com/pay/api/web/reference/request-objects#MerchantInfo">
              <code>MerchantInfo</code>
            </a>
            object, set <code>merchantId</code> to your Google Pay merchant ID obtained in step 1.
          </p>
          <pre>
{
  "merchantInfo": {
    "merchantId": "12345"
  }
}
            </pre>

          <p>
            When building the
            <a target="_blank"
               href="https://developers.google.com/pay/api/web/reference/request-objects#PaymentMethodTokenizationSpecification">
              <code>TokenizationSpecification</code>
            </a>
            object, set <code>gateway</code> to <code>"acceptblue"</code>,
            and <code>gatewayMerchantId</code> to your uniquely generated Google Pay Gateway Merchant ID.
          </p>
          <p>
            To find your Google Pay key, navigate to the Control Panel page in your merchant portal,
            and choose the Digital Wallet option.
            Then scroll to the Google Pay section. Your Google Pay Gateway Merchant ID will be displayed.
            This ID is unique to you, and will not change.
          </p>
          <pre>
{
  "tokenizationSpecification": {
    "type": "PAYMENT_GATEWAY",
    "parameters": {
      "gateway": "acceptblue",
      "gatewayMerchantId": "GOOGLE_PAY_GATEWAY_MERCHANT_ID_HERE"
    }
  }
}
            </pre>

          <p>
            You can use the
            <a target="_blank"
               href="https://developers.google.com/pay/api/web/guides/test-and-deploy/integration-checklist">
              Google Pay Web integration checklist
            </a>
            to help you build the integration.
          </p>

          <p>
            All integrations must comply with the
            <a target="_blank" href="https://developers.google.com/pay/api/web/guides/brand-guidelines">
              Google Pay Web Brand Guidelines
            </a>.
          </p>
        </li>
        <li>
          <h4> Handling the Google Pay payload</h4>
          <p>
            When the customer uses your page to check out with Google Pay, Google Pay will issue a
            <a target="_blank" href="https://developers.google.com/pay/api/web/reference/response-objects#PaymentData">
              <code>PaymentData</code>
            </a>
            response object that includes encrypted data containing the payment data.
            The encrypted data can be extracted from the <code>paymentMethodData.tokenizationData</code> property of
            the <code>PaymentData</code> response object.
          </p>
        </li>
        <li>
          <h4> Submitting the charge request</h4>
          <p>
            Validate the charge request and amount on your backend. Then, create a
            <a target="_blank" href="/api/v2#tag/processing-charges/paths/~1transactions~1charge/post">
              Digital Wallet Charge
            </a>
            request object containing the encrypted payload obtained from Google Pay.
            The <code>source</code> field should be set to <code>"googlepay"</code>, and the
            encrypted data should be passed in the <code>token</code> field.
          </p>
          <p><strong>Note</strong>: The encrypted data only contains the information needed to
            process a charge using the customer's saved wallet. You will still need to supply the
            charge amount and any billing information, if applicable.
          </p>
          <pre>
{
  "amount": 1.5,
  "source": "googlepay",
  "token": "examplePaymentMethodToken",
  "avs_zip": "12345",
  "avs_address": "123 Main St."
}
            </pre>
          <p>Finally, submit request to the gateway using the
            <a href="/api/v2#tag/processing-charges/paths/~1transactions~1charge/post">
              /charge endpoint
            </a>.
          </p>
          <pre>
curl --request POST \
  --url API_DOMAIN/api/v2/transactions/charge \
  --user 'API_KEY:API_KEY_PIN' \
  --header 'Content-Type: application/json' \
  --data '{
    "amount": 1.5,
    "source": "googlepay",
    "token": "examplePaymentMethodToken",
    "avs_zip": "12345",
    "avs_address": "123 Main St."
  }'
                </pre>
        </li>
      </ol>
    </section>
  </div>
</template>

<style lang="scss" scoped>
@import '../../styles/bulma-wrapper.scss';

pre {
  margin: 20px 3rem 20px 0;
}

ol.bottom-margin > li {
  margin-bottom: 20px;
}

.body-wrapper {
  width: 80%;
  margin: auto;
}

section {
  margin-bottom: 25px;
}

a > code {
  text-decoration: underline;
}
</style>
